import { Minimize2 } from "lucide-react";
import { CSSProperties } from "react";
import { Link } from "react-router";

import { PageSection, PageSectionAction, PageSectionBadge } from "nazare-ui/components/page-section";

import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { SkeletonRow } from "../../../../skeleton-row";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useDocumentTitle } from "../../../../use-document-title";
import { useActiveWorkflows, useActiveWorkflowsStream, useWorkflowDefinitions } from "../../../../workflows-api";
import styles from "./active.module.css";
import { StatusChip } from "./run-detail-status-chip";
import { workflowRunPauseState } from "../../../../workflow-run-control";
import { workflowDisplayLabel } from "../display-labels";

function elapsed(iso: string | null) {
  if (!iso) return "—";
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

export function WorkflowActiveRunsPage() {
  useDocumentTitle("Active Runs");
  const query = useActiveWorkflows();
  const definitions = useWorkflowDefinitions();
  useActiveWorkflowsStream();
  const rows = query.data?.runs ?? [];
  const queuedCount = query.data?.byStatus.queued ?? query.data?.byStatus.pending ?? 0;
  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  return (
    <div className={styles.page}>
      <PageSection
        className={styles.card}
        style={{ viewTransitionName: "wf-card-active" } as CSSProperties}
        title="Active runs"
        count={query.data ? query.data.total : undefined}
        badges={<>
          {query.data?.activeSteps !== undefined && <PageSectionBadge title="Currently executing workflow steps">{query.data.activeSteps.toLocaleString("en-GB")} active steps</PageSectionBadge>}
          {queuedCount > 0 && <PageSectionBadge title="Runs waiting to start">{queuedCount.toLocaleString("en-GB")} queued</PageSectionBadge>}
        </>}
        actions={
          <PageSectionAction asChild aria-label="Minimise to dashboard" title="Minimise to dashboard">
            <Link to="/workflows" viewTransition><Minimize2 aria-hidden="true" /></Link>
          </PageSectionAction>
        }
      >
        <div className={styles.scrollShadowWrapper}>
          <div ref={tableWrapRef} className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th className={styles.th}>Status</th><th className={styles.th}>Workflow</th><th className={styles.th}>Run ID</th><th className={`${styles.th} ${styles.thRight}`}>Elapsed</th></tr></thead>
              <tbody>
                {query.isLoading ? Array.from({ length: 8 }, (_, index) => <SkeletonRow key={index} columns={4} />) : query.error ? (
                  <tr><td className={styles.emptyCell} colSpan={4}>Couldn’t load active runs.</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td className={styles.emptyCell} colSpan={4}>No active runs.</td></tr>
                  ) : rows.map((run) => (
                    <tr key={run.runId} className={styles.row}>
                    <td className={styles.td}>
                      <Link className={styles.rowLink} to={`/workflows/runs/${encodeURIComponent(run.runId)}`} aria-label={`View run ${run.runId}`} />
                      <span className={styles.statusCell}><StatusChip status={run.status} pauseState={workflowRunPauseState(run)} /></span>
                    </td>
                    <td className={styles.td}>{workflowDisplayLabel(run, definitions.data?.definitions)}{run.label ? ` · ${run.label}` : ""}</td>
                    <td className={`${styles.td} ${styles.runId}`}>{run.runId}</td>
                    <td className={`${styles.td} ${styles.elapsed}`}>{elapsed(run.startedAt ?? run.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </PageSection>
    </div>
  );
}
